import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

const Pagination = ({
  pagination,
  onPageChange,
  onPageSizeChange,
  pageSize,
  pageSizeOptions = [10, 15, 20],
  showInfo = true,
  className = ""
}) => {
  const {
    total_records: totalRecords,
    total_pages: totalPages,
    current_page: currentPage,
    has_next: hasNext,
    has_previous: hasPrevious
  } = pagination;

  // Calculate the range of records being displayed
  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalRecords);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages if total pages is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show pages around current page
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, currentPage + 2);
      
      // Add first page if not included
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }
      
      // Add pages around current
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add last page if not included
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0 ${className}`}>
      {/* Records Info */}
      {showInfo && (
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-700">
            Showing {startRecord} to {endRecord} of {totalRecords} results
          </div>
          
          {/* Page Size Selector */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">Show:</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
              className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      
      {/* Navigation Controls */}
      <div className="flex items-center space-x-1">
        {/* First Page */}
        <button
          onClick={() => onPageChange(1)}
          disabled={!hasPrevious}
          className={`p-2 rounded-full border transition-all duration-200 ${
            hasPrevious
              ? 'border-gray-300 text-gray-500 hover:bg-gray-50'
              : 'border-gray-200 text-gray-300 cursor-not-allowed'
          }`}
          title="First page"
        >
          <ChevronsLeft className="w-4 h-4" />
        </button>
        
        {/* Previous Page */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!hasPrevious}
          className={`p-2 rounded-full border transition-all duration-200 ${
            hasPrevious
              ? 'border-gray-300 text-gray-500 hover:bg-gray-50'
              : 'border-gray-200 text-gray-300 cursor-not-allowed'
          }`}
          title="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        
        {/* Page Numbers */}
        <div className="flex items-center space-x-1">
          {pageNumbers.map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span className="px-3 py-2 text-gray-500">...</span>
              ) : (
                <button
                  onClick={() => onPageChange(page)}
                  className={`px-3 py-2 rounded-full border text-sm transition-all duration-200 ${
                    page === currentPage
                      ? 'border-blue-500 bg-blue-50 text-blue-600'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>
        
        {/* Next Page */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!hasNext}
          className={`p-2 rounded-full border transition-all duration-200 ${
            hasNext
              ? 'border-gray-300 text-gray-500 hover:bg-gray-50'
              : 'border-gray-200 text-gray-300 cursor-not-allowed'
          }`}
          title="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        
        {/* Last Page */}
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={!hasNext}
          className={`p-2 rounded-full border transition-all duration-200 ${
            hasNext
              ? 'border-gray-300 text-gray-500 hover:bg-gray-50'
              : 'border-gray-200 text-gray-300 cursor-not-allowed'
          }`}
          title="Last page"
        >
          <ChevronsRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;